import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { UserRole, RolePermissions } from '../types'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: UserRole
  requiredPermissions?: Partial<RolePermissions>
  fallbackPath?: string
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermissions,
  fallbackPath = '/login'
}) => {
  const { isAuthenticated, user, getUserRole, getUserPermissions } = useAuthStore()
  const location = useLocation()

  // Check if user is authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />
  }

  const userRole = getUserRole()
  const userPermissions = getUserPermissions()

  // Check role-based access
  if (requiredRole && userRole !== requiredRole) {
    // Redirect to appropriate dashboard based on user's actual role
    const roleBasedPath = getRoleBasedPath(userRole)
    return <Navigate to={roleBasedPath} replace />
  }

  // Check permission-based access
  if (requiredPermissions) {
    const hasRequiredPermissions = Object.entries(requiredPermissions).every(
      ([permission, required]) => {
        const userHasPermission = userPermissions[permission as keyof RolePermissions]
        return !required || userHasPermission
      }
    )

    if (!hasRequiredPermissions) {
      // Redirect to appropriate dashboard based on user's actual role
      const roleBasedPath = getRoleBasedPath(userRole)
      return <Navigate to={roleBasedPath} replace />
    }
  }

  return <>{children}</>
}

// Helper function to get role-based default path
const getRoleBasedPath = (role: UserRole | null): string => {
  switch (role) {
    case 'ADMIN':
      return '/admin'
    case 'PENTESTER':
      return '/pentester'
    case 'TENANT_ADMIN':
      return '/tenant'
    default:
      return '/'
  }
}

export default ProtectedRoute
